import { Breadcrumb2Container } from "app/components/breadcrumb2/Breadcrumb2Container"
import { useAppSelector } from "app/redux/hooks"
import { IItemOrcamento } from "app/types/IItemOrcamento"
import { Parametro, ParametroPorItem } from "app/types/Parametro"
import { Permissao } from "app/types/Permissao"
import { obterPermissao } from "app/use-cases/obterPermissao"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { DetalheItem } from "./DetalheItem"
import { obterParametros, obterParametrosPorOidItem } from "app/use-cases/parametro/obterParametros"
import globalMU from "globalMU"


export type DetalheItemContainerProps = {
    item: IItemOrcamento
    onVoltar: () => void
}

const PASSOS_BREADCRUMB = ["Itens", "Detalhes do Produto"]

const NOMES_PARAMETROS = [
    "ORCAMENTO_OUTRAS_SAIDAS_RETIRADA_OUTRO_LOCAL",
    "VENDA_COM_ENTREGA_POSTERIOR_POR_ITEM",
    "DEFINIR_PREVISAO_ENTREGA_POR_ITEM",
    "EXIBIR_QUANTIDADE_ESTOQUE_CONSULTA_PRODUTOS",
    "AMBIENTE_ORCAMENTO",
    "EXIBIR_PRECO_COMISSAO_NA_CONSULTA_PRODUTOS",
    "EXIBIR_QUANTIDADE_MINIMA_VENDA_CONSULTA_PRODUTOS",
    "APURAR_ST_ORCAMENTO",
    // Os 3 abaixo eram LIDOS por valorParametro mas nunca eram PEDIDOS, então vinham sempre
    // undefined: CASAS_DECIMAIS_PRECO_VENDA fazia Number(undefined) => NaN virar a máscara de preço,
    // e CONTROLE_VALIDADE_PRODUTOS deixava a condição de mostrarLocalRetirada em Outras Saídas morta.
    "CASAS_DECIMAIS_PRECO_VENDA",
    "ORCAMENTO_MASCARA_QUANTIDADE",
    "CONTROLE_VALIDADE_PRODUTOS",
]

export const DetalheItemContainer = (props: DetalheItemContainerProps) => {
    const orcamento = useAppSelector((store) => store.orcamento)
    const [permissoes, setPermissoes] = useState<Permissao[]>([])
    const [parametros, setParametros] = useState<Parametro[]>([])
    const [parametrosPorFilial, setParametrosPorFilial] = useState<ParametroPorItem[]>([]) // 433263
    const [parametrosCarregados, setParametrosCarregados] = useState(false)
    const [numeroCasasDecimaisPrecoVenda, setNumeroCasasDecimaisPrecoVenda] = useState(2)
    const [numeroCasasDecimaisQuantidade, setNumeroCasasDecimaisQuantidade] = useState(3)

    const isMounted = useRef(true)

    const atualizarPermissoes = async () => {
        const permitidoMostrarLocalRetirada = await obterPermissao({
            funcionalidade: "ORCAMENTO_ITEM_VENDA",
            acao: "MOSTRAR_LOCAL_RETIRADA",
        })

        const parametrosDaFilial = await obterParametrosPorOidItem({ // 433263
            nomes: ["UTILIZA_RECURSO_ENCOMENDA_EXPRESSA"],
            oidItem: orcamento.OidFilialOrcamento,
        })

        if (!isMounted.current) return

        setPermissoes([
            { Acao: "MOSTRAR_LOCAL_RETIRADA", Permitido: permitidoMostrarLocalRetirada },
        ])
        setParametrosPorFilial(parametrosDaFilial) // 433263
    }

    const atualizarValoresParametros = async () => {
        const parametrosObtidos = await obterParametros({ nomes: NOMES_PARAMETROS })

        if (!isMounted.current) return

        setParametros(parametrosObtidos)

        // As casas decimais são derivadas de parametrosObtidos, NÃO do state `parametros`: o state só
        // enxerga o valor novo no render seguinte, então ler dele aqui pegava sempre o array do
        // render anterior (vazio na primeira carga) e as duas máscaras caíam no default/NaN.
        const casasDecimaisPrecoVenda = Number(
            parametrosObtidos.find(p => p.Nome == "CASAS_DECIMAIS_PRECO_VENDA")?.Valor
        )

        setNumeroCasasDecimaisPrecoVenda(
            Number.isFinite(casasDecimaisPrecoVenda) && casasDecimaisPrecoVenda > 0
                ? casasDecimaisPrecoVenda
                : 2
        )

        setNumeroCasasDecimaisQuantidade(
            globalMU.resolverNumeroCasasDecimaisDeParametroFormatado(
                parametrosObtidos.find(p => p.Nome == "ORCAMENTO_MASCARA_QUANTIDADE")?.Valor,
                3
            )
        )
    }

    const valorParametro = useCallback(
        (nome: string) => parametros.find((p) => p.Nome == nome)?.Valor,
        [parametros]
    )

    const valorParametroPorFilial = useCallback( // 433263
        (nome: string) => parametrosPorFilial.find((p) => p.Nome == nome)?.Valor,
        [parametrosPorFilial]
    )

    const onPassoSelecionado = useCallback((passo: string) => {
        if (passo == "Itens") {
            props.onVoltar()
        }
    }, [props.onVoltar])

    useEffect(() => {
        isMounted.current = true

        // parametrosCarregados só pode ligar DEPOIS das duas cargas: antes ele era setado de forma
        // síncrona logo após disparar as promises, então o DetalheItem renderizava uma primeira vez
        // com permissões e parâmetros vazios (local de retirada oculto, máscaras no default).
        const carregar = async () => {
            await Promise.all([atualizarPermissoes(), atualizarValoresParametros()])

            if (isMounted.current) {
                setParametrosCarregados(true)
            }
        }

        carregar()

        return () => {
            isMounted.current = false
        }
    }, [])

    const propsDetalheItem = useMemo(() => ({
        tipoOrcamento: orcamento.TipoOrcamentoDescricao,
        mostrarLocalRetirada:
            permissoes?.find((permissao) => permissao.Acao == "MOSTRAR_LOCAL_RETIRADA")?.Permitido
            && (orcamento.TipoOrcamentoDescricao == "Venda"
                || (orcamento.TipoOrcamentoDescricao == "Outras Saídas"
                    && valorParametro("CONTROLE_VALIDADE_PRODUTOS") == "S")),
        usarVendaComEntergaPosterior: valorParametro("VENDA_COM_ENTREGA_POSTERIOR_POR_ITEM") == "S",
        tratarVendaSobEncomenda:
            valorParametro("DEFINIR_PREVISAO_ENTREGA_POR_ITEM") != "0"
            && valorParametro("DEFINIR_PREVISAO_ENTREGA_POR_ITEM") != ""
            && valorParametro("DEFINIR_PREVISAO_ENTREGA_POR_ITEM") != null,
        utilizaRecursoEncomendaExpressa: valorParametroPorFilial("UTILIZA_RECURSO_ENCOMENDA_EXPRESSA") == "S", // 433263
        exibirQuantidadeEstoqueConsultaProduto: valorParametro("EXIBIR_QUANTIDADE_ESTOQUE_CONSULTA_PRODUTOS") == "S",
        ambienteOrcamento: valorParametro("AMBIENTE_ORCAMENTO") == "S",
        exibirPrecoComissaoConsultasProdutos: valorParametro("EXIBIR_PRECO_COMISSAO_NA_CONSULTA_PRODUTOS") == "S",
        exibirQuantidadeMinimaVendaConsultaProdutos: valorParametro("EXIBIR_QUANTIDADE_MINIMA_VENDA_CONSULTA_PRODUTOS") == "S",
        apurarSTOrcamento: valorParametro("APURAR_ST_ORCAMENTO") == "S",
        mascaraQuantidade: numeroCasasDecimaisQuantidade,
        mascaraPrecoVenda: numeroCasasDecimaisPrecoVenda == 0 ? 2 : numeroCasasDecimaisPrecoVenda,
    }), [
        orcamento.TipoOrcamentoDescricao,
        permissoes,
        valorParametro,
        valorParametroPorFilial,
        numeroCasasDecimaisQuantidade,
        numeroCasasDecimaisPrecoVenda,
    ])

    if (!parametrosCarregados) {
        return null
    }

    return (
        <>
            <Breadcrumb2Container
                passos={PASSOS_BREADCRUMB}
                id="DetalheItemBreadcrumb"
                exibirBotaoLimpar={false}
                exibirBotaoVoltar={true}
                onPassoSelecionado={onPassoSelecionado}
                onBotaoVoltarClicado={props.onVoltar}
            />

            <DetalheItem item={props.item} {...propsDetalheItem} />
        </>
    )
}
